import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { Btn } from "./common";
import { MultiSelect } from "./ProfileFields";

const OPS = {
  text: [["eq", "Equals"], ["contains", "Contains"], ["starts", "Starts with"]],
  select: [["eq", "Equals"]],
  number: [["eq", "="], ["gt", ">"], ["gte", ">="], ["lt", "<"], ["lte", "<="], ["between", "Between"]],
  date: [["on", "On"], ["before", "Before"], ["after", "After"], ["between", "Between"]],
  multiselect: [["any", "Includes any selected"], ["all", "Includes all selected"], ["none", "Does not include"]],
  bool: [["yes", "Is Yes"], ["no", "Is No"]],
};
const cls = "rounded-md border border-slate-300 bg-white px-2 py-2 text-sm";
const empty = () => ({ field: "", op: "", value: "" });

function ValueInput({ f, c, set, i }) {
  if (!f || f.type === "bool") return <span className="text-xs text-slate-400 self-center">—</span>;  const tid = `filter-value-${i}`;
  if (f.type === "select") return <select className={`${cls} flex-1`} value={c.value} onChange={(e) => set({ value: e.target.value })} data-testid={tid}><option value="">Select…</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select>;
  if (f.type === "multiselect") return <div className="flex-1"><MultiSelect f={{ ...f, key: `filter-${i}`, exclusive: null }} value={c.value || []} onChange={(v) => set({ value: v })} /></div>;
  const type = f.type === "number" ? "number" : f.type === "date" ? "date" : "text";
  if (c.op === "between") {
    const v = Array.isArray(c.value) ? c.value : ["", ""];
    return <div className="flex flex-1 gap-1"><input type={type} className={`${cls} w-full`} value={v[0]} onChange={(e) => set({ value: [e.target.value, v[1]] })} data-testid={`${tid}-from`} aria-label="From" /><input type={type} className={`${cls} w-full`} value={v[1]} onChange={(e) => set({ value: [v[0], e.target.value] })} data-testid={`${tid}-to`} aria-label="To" /></div>;
  }
  return <input type={type} className={`${cls} flex-1`} value={c.value} onChange={(e) => set({ value: e.target.value })} placeholder="Required" data-testid={tid} aria-label="Value" />;
}

export default function FilterDialog({ module, open, initial, onClose, onApply }) {
  const [rows, setRows] = useState([empty()]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setRows(initial.length ? initial.map((x) => ({ ...x })) : [empty()]); setErr(""); } }, [open, initial]);
  const fmap = Object.fromEntries(module.filter_fields.map((f) => [f.key, f]));
  const upd = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const filled = (r) => {
    const f = fmap[r.field];
    if (!f || !r.op) return false;
    if (f.type === "bool") return true;
    if (Array.isArray(r.value)) return r.value.length > 0 && r.value.every((x) => x !== "");
    return String(r.value).trim() !== "";
  };
  const apply = async () => {
    if (!rows.length || !rows.every(filled)) { setErr("Complete every condition (field, operator and value) or remove it."); return; }
    const out = rows.map((r) => ({ field: r.field, op: r.op, value: fmap[r.field].type === "bool" ? null : r.value }));
    setBusy(true); setErr("");
    try {
      await api.get(`/m/${module.key}/records`, { params: { filters: JSON.stringify(out), size: 1 } });
      onApply(out);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="filter-dialog">
        <DialogHeader><DialogTitle>Filter {module.name}</DialogTitle><DialogDescription>All conditions are combined with AND. Filtering runs on the server for your LGU only.</DialogDescription></DialogHeader>
        <div className="space-y-2">
          {rows.map((c, i) => {
            const f = fmap[c.field];
            return (
              <div key={i} className="flex flex-wrap sm:flex-nowrap gap-2 items-start" data-testid={`filter-row-${i}`}>
                <select className={`${cls} sm:w-56`} value={c.field} onChange={(e) => { const nf = fmap[e.target.value]; upd(i, { field: e.target.value, op: nf ? OPS[nf.type][0][0] : "", value: "" }); }} data-testid={`filter-field-${i}`} aria-label="Field">
                  <option value="">Select field…</option>
                  {module.filter_fields.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                </select>
                <select className={`${cls} sm:w-44`} value={c.op} disabled={!f} onChange={(e) => upd(i, { op: e.target.value, value: "" })} data-testid={`filter-op-${i}`} aria-label="Operator">
                  {(f ? OPS[f.type] : []).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <ValueInput f={f} c={c} i={i} set={(p) => upd(i, p)} />
                <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 text-red-700" aria-label="Remove condition" data-testid={`filter-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            );
          })}
          <Btn type="button" variant="outline" className="text-xs" onClick={() => setRows([...rows, empty()])} data-testid="filter-add-condition-btn"><Plus className="h-3.5 w-3.5" />Add condition</Btn>
        </div>
        {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2" data-testid="filter-error">{err}</p>}
        <div className="flex justify-between gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={() => setRows([empty()])} data-testid="filter-clear-all-btn">Clear all</Btn>
          <div className="flex gap-2"><Btn type="button" variant="outline" onClick={onClose} data-testid="filter-cancel-btn">Cancel</Btn>
            <Btn type="button" disabled={busy} onClick={apply} data-testid="filter-apply-btn">{busy ? "Applying…" : "Apply filters"}</Btn></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
